/**
 * The filter bar. **All filter state lives in the URL query string** (Phase 4),
 * so a filtered view is a link.
 *
 * Two labels here are not decoration:
 *
 *  - the **business-unit filter is an `EXISTS` over lines**, so a multi-BU
 *    opportunity appears under each BU it touches — which means summing the
 *    filtered totals across BUs exceeds the unfiltered total. The UI says so,
 *    because someone will otherwise file it as a bug;
 *  - when the filter pins one `price_basis`, a single indicator says which,
 *    rather than repeating a tag on every value cell.
 */

import { Search, X } from 'lucide-react';
import {
  FLAG_FILTER_LABELS,
  FOCUS_HELP_TEXT,
  FORECAST_CATEGORIES,
  OPPORTUNITY_FLAG_FILTER_KEYS,
  OPPORTUNITY_STATUSES,
  PRICE_BASES,
  REGIONS,
  SEGMENTS,
  STALL_PREDICATES,
  VISIBLE_STATUSES,
} from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { humanize } from '@/lib/format';
import { useBusinessUnits, useReferenceData, useStages } from '@/lib/reference';
import { STALL_LABELS } from '@/components/domain/RiskBadges';
import { AccountFilter } from '@/features/pickers/AccountFilter';
import { ALL_STATUSES, DEFAULT_STATUSES, type FilterKey, type ListState } from '@/lib/list-filters';

export interface FilterBarProps {
  state: ListState;
  setFilter: (key: FilterKey, value: string | null) => void;
  setMany: (updates: Record<string, string | null>) => void;
  clearFilters: () => void;
  activeFilterCount: number;
}

export function FilterBar({
  state,
  setFilter,
  setMany,
  clearFilters,
  activeFilterCount,
}: FilterBarProps): React.ReactElement {
  const reference = useReferenceData();
  const businessUnits = useBusinessUnits();
  const { list: stages } = useStages();
  const { filters } = state;

  return (
    <div className="space-y-2 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search opportunities"
            placeholder="Search name, account, or opp number…"
            className="pl-8"
            defaultValue={state.term}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setMany({ q: (event.target as HTMLInputElement).value });
              }
            }}
            onBlur={(event) => setMany({ q: event.target.value })}
          />
        </div>

        <Filter
          label="Stage"
          value={filters.stage ?? ''}
          onChange={(value) => setFilter('stage', value)}
          options={stages.map((stage) => ({ value: stage.code, label: stage.name }))}
        />
        <StatusFilter value={filters.status} onChange={(value) => setFilter('status', value)} />
        <div className="text-xs">
          <label htmlFor="opportunity-account-filter" className="sr-only">
            Account
          </label>
          <AccountFilter
            id="opportunity-account-filter"
            value={filters.accountId}
            scope={filters.accountScope}
            onChange={(value) => setMany({ accountId: value, accountScope: null })}
          />
        </div>
        <Filter
          label="Owner"
          value={filters.ownerId ?? ''}
          onChange={(value) => setFilter('ownerId', value)}
          options={reference.users.map((user) => ({ value: user.id, label: user.display_name }))}
        />
        <Filter
          label="FAE"
          value={filters.faeId ?? ''}
          onChange={(value) => setFilter('faeId', value)}
          options={reference.users.map((user) => ({ value: user.id, label: user.display_name }))}
        />
        <Filter
          label="Rep firm"
          value={filters.repFirmId ?? ''}
          onChange={(value) => setFilter('repFirmId', value)}
          options={reference.rep_firms.map((firm) => ({ value: firm.id, label: firm.name }))}
        />
        <Filter
          label="BU mix"
          value={filters.businessUnit ?? ''}
          onChange={(value) => setFilter('businessUnit', value)}
          options={businessUnits.options.map((unit) => ({ value: unit.code, label: unit.label }))}
        />
        <Filter
          label="Basis"
          value={filters.priceBasis ?? ''}
          onChange={(value) => setFilter('priceBasis', value)}
          options={PRICE_BASES.map((value) => ({ value, label: humanize(value) }))}
        />
        <Filter
          label="Segment"
          value={filters.segment ?? ''}
          onChange={(value) => setFilter('segment', value)}
          options={SEGMENTS.map((value) => ({ value, label: humanize(value) }))}
        />
        <Filter
          label="Region"
          value={filters.region ?? ''}
          onChange={(value) => setFilter('region', value)}
          options={REGIONS.map((value) => ({ value, label: value }))}
        />
        <Filter
          label="Forecast"
          value={filters.forecastCategory ?? ''}
          onChange={(value) => setFilter('forecastCategory', value)}
          options={FORECAST_CATEGORIES.map((value) => ({ value, label: humanize(value) }))}
        />

        {OPPORTUNITY_FLAG_FILTER_KEYS.map((key) => (
          <Filter
            key={key}
            label={FLAG_FILTER_LABELS[key].label}
            value={filters[key] ?? ''}
            onChange={(value) => setFilter(key, value)}
            options={[
              { value: 'yes', label: FLAG_FILTER_LABELS[key].yes },
              { value: 'no', label: FLAG_FILTER_LABELS[key].no },
            ]}
            allLabel={FLAG_FILTER_LABELS[key].any}
            title={key === 'focus' ? FOCUS_HELP_TEXT : undefined}
          />
        ))}

        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={filters.stalled !== undefined}
            onChange={(event) => setFilter('stalled', event.target.checked ? 'any' : null)}
          />
          Stalled only
        </label>

        {filters.stalled !== undefined && (
          <Filter
            label="Signal"
            value={filters.stalled === 'any' ? '' : filters.stalled}
            onChange={(value) => setFilter('stalled', value ?? 'any')}
            options={STALL_PREDICATES.map((value) => ({ value, label: STALL_LABELS[value] }))}
            allLabel="Any signal"
          />
        )}

        <div className="flex items-center gap-1.5">
          <Input
            aria-label="Minimum annual revenue (net)"
            placeholder="Min $"
            className="h-9 w-24"
            inputMode="numeric"
            defaultValue={filters.minValue ?? ''}
            onBlur={(event) => setFilter('minValue', event.target.value || null)}
          />
          <Input
            aria-label="Maximum annual revenue (net)"
            placeholder="Max $"
            className="h-9 w-24"
            inputMode="numeric"
            defaultValue={filters.maxValue ?? ''}
            onBlur={(event) => setFilter('maxValue', event.target.value || null)}
          />
        </div>

        <div className="flex items-center gap-1.5">
          <Input
            aria-label="Decision date before"
            type="date"
            className="h-9 w-36"
            defaultValue={filters.closingBefore ?? ''}
            onChange={(event) => setFilter('closingBefore', event.target.value || null)}
          />
          <Input
            aria-label="Ramp between"
            placeholder="ramp YYYY-MM-DD..YYYY-MM-DD"
            className="h-9 w-56"
            defaultValue={filters.rampBetween ?? ''}
            onBlur={(event) => setFilter('rampBetween', event.target.value || null)}
          />
        </div>

        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {filters.priceBasis !== undefined && (
        <p className="text-xs text-muted-foreground">
          <Badge variant="info">Basis pinned</Badge> Every value below is on the{' '}
          {humanize(filters.priceBasis)} basis.
        </p>
      )}

      {filters.businessUnit !== undefined && (
        <p className="text-xs text-muted-foreground">
          The business-unit filter matches any line in that unit, so an opportunity spanning two
          units appears under both — summing filtered totals across business units will exceed the
          unfiltered total.
        </p>
      )}
    </div>
  );
}

function Filter({
  label,
  value,
  onChange,
  options,
  allLabel,
  title,
}: {
  label: string;
  value: string;
  onChange: (value: string | null) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
  title?: string;
}): React.ReactElement {
  return (
    <label className="flex items-center gap-1 text-xs" title={title}>
      <span className="sr-only">{label}</span>
      <Select
        aria-label={label}
        className="h-9 w-auto min-w-[7rem]"
        value={value}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">{allLabel ?? label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </label>
  );
}

/**
 * Status is **multi-select** (docs/bugs.md 2026-10-10, session 1 part 3), so
 * Lost alone, or any combination, can be chosen. The selection is written to
 * the URL in canonical order, so the same choice is always the same link:
 *
 *  - the default (Open + Won) is written as NO `status`, the URL it came from;
 *  - every status is written as `status=all`, because an absent status now
 *    means the default;
 *  - anything else is the CSV include list the API already accepts.
 *
 * **"Every status" means every status except Invalid** (decision 12): the
 * API leaves Invalid out when no status is named, and `status=all` names none.
 * Invalid is shown only when its box is ticked, which writes an explicit list.
 *
 * The last ticked box cannot be unticked: an empty selection would be a list
 * that can never show anything.
 */
function StatusFilter({
  value,
  onChange,
}: {
  /** The effective CSV from `ListState`; `undefined` means every status. */
  value: string | undefined;
  onChange: (value: string | null) => void;
}): React.ReactElement {
  const selected: readonly string[] =
    value === undefined ? VISIBLE_STATUSES : value.split(',').filter((s) => s !== '');

  const sameAs = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && b.every((s) => a.includes(s));

  const summary = sameAs(selected, VISIBLE_STATUSES)
    ? 'All statuses'
    : OPPORTUNITY_STATUSES.filter((s) => selected.includes(s))
        .map(humanize)
        .join(', ');

  const toggle = (status: string, on: boolean): void => {
    const next = OPPORTUNITY_STATUSES.filter((s) => (s === status ? on : selected.includes(s)));
    if (next.length === 0) return;
    if (sameAs(next, DEFAULT_STATUSES)) onChange(null);
    else if (sameAs(next, VISIBLE_STATUSES)) onChange(ALL_STATUSES);
    else onChange(next.join(','));
  };

  return (
    <details className="relative text-xs">
      <summary className="flex h-9 min-w-[7rem] cursor-pointer list-none items-center rounded-md border border-input bg-background px-3">
        Status: {summary}
      </summary>
      <fieldset className="absolute left-0 z-20 mt-1 min-w-[11rem] space-y-1 rounded-md border bg-card p-2 shadow-md">
        <legend className="sr-only">Status</legend>
        {OPPORTUNITY_STATUSES.map((status) => {
          const checked = selected.includes(status);
          return (
            <label key={status} className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={checked}
                disabled={checked && selected.length === 1}
                onChange={(event) => toggle(status, event.target.checked)}
              />
              {humanize(status)}
            </label>
          );
        })}
      </fieldset>
    </details>
  );
}
